// src/features/userManagement/components/InviteUserModal.jsx

import React, { useState, useEffect, useCallback } from 'react';
import ScreenPermissionSelector, { ACCESS_READ } from './ScreenPermissionSelector';
import { listAllScreens, listRoleScreens } from '../services/screenService';

const EMPTY = {
  username: '',
  password: '',
  email: '',
  firstname: '',
  lastname: '',
  user_type: 'ORG_USER',
  role_id: '',
};

export default function InviteUserModal({ open, submitting, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY);
  const [allScreens, setAllScreens] = useState([]);
  const [screensLoading, setScreensLoading] = useState(false);
  const [screenMap, setScreenMap] = useState({}); // screenId -> 'READ' | 'READ_WRITE'

  // Load the full screen catalog when the modal opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setScreensLoading(true);
    listAllScreens()
      .then((data) => { if (!cancelled) setAllScreens(data || []); })
      .catch(() => { if (!cancelled) setAllScreens([]); })
      .finally(() => { if (!cancelled) setScreensLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Seed screen defaults from the selected role (READ for each of the role's screens).
  const seedFromRole = useCallback(async (roleId) => {
    if (!roleId) return;
    try {
      const roleScreens = await listRoleScreens(roleId);
      const seeded = {};
      (roleScreens || []).forEach((s) => { seeded[s.id] = ACCESS_READ; });
      setScreenMap(seeded);
    } catch {
      // role has no screens / lookup failed - leave selection untouched
    }
  }, []);

  if (!open) return null;

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleRoleChange = (value) => {
    set('role_id', value);
    if (value) seedFromRole(Number(value));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      role_id: form.role_id ? Number(form.role_id) : null,
      screens: Object.entries(screenMap).map(([screen_id, access_level]) => ({
        screen_id: Number(screen_id),
        access_level,
      })),
    };
    onSubmit(payload);
  };

  const handleClose = () => {
    setForm(EMPTY);
    setScreenMap({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-base font-semibold text-gray-900">Invite Sub-User</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="px-6 py-5 space-y-4 overflow-y-auto">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username *</label>
              <input
                type="text"
                required
                value={form.username}
                onChange={e => set('username', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. john.doe"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <input
                type="password"
                required
                value={form.password}
                onChange={e => set('password', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={e => set('email', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={form.firstname}
                  onChange={e => set('firstname', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={form.lastname}
                  onChange={e => set('lastname', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* User Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User Type *</label>
                <select
                  value={form.user_type}
                  onChange={e => set('user_type', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="ORG_USER">Org User</option>
                  <option value="ORG_ADMIN">Org Admin</option>
                </select>
              </div>

              {/* Role ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role ID <span className="text-gray-400 font-normal">(seeds screens)</span>
                </label>
                <input
                  type="number"
                  value={form.role_id}
                  onChange={e => handleRoleChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Optional"
                />
              </div>
            </div>

            {/* Screen permissions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Screen Permissions</label>
              <p className="text-xs text-gray-400 mb-2">
                Org Admins see every screen regardless of this selection. For Org Users, only checked screens are visible.
              </p>
              <ScreenPermissionSelector
                allScreens={allScreens}
                value={screenMap}
                onChange={setScreenMap}
                loading={screensLoading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
